import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import SplitText from 'gsap/dist/SplitText'

import { splitLines, SplitTextEngine } from '@/utils/split-text'

export type SharedAnimation = {
  timeline: gsap.core.Timeline
  revert: () => void
}

export const getScrollTriggerOptions = (
  node: HTMLElement,
  reverse: boolean,
  start = 'top 82%' // 'top 92%'
) => {
  return {
    scrollTrigger: {
      trigger: node,
      once: !reverse,
      start,
      markers: false,
      invalidateOnRefresh: true,
      toggleActions: `play none none ${reverse ? 'reverse' : 'none'}`
    }
  }
}

export const linesRevealAnimation = (
  target: HTMLElement,
  reverse?: boolean,
  splitTextEngine: SplitTextEngine = SplitTextEngine.GSAP
): SharedAnimation => {
  // If target is a Typography wrapper ([data-typography]), find its inner
  // [data-typography-child] span and force block display so SplitText can
  // detect line breaks (inline-flex from CSS prevents this).
  let child: HTMLElement = target
  const typographyEl = target.hasAttribute('data-typography')
    ? target
    : target.querySelector('[data-typography]')

  if (typographyEl instanceof HTMLElement) {
    const inner = typographyEl.querySelector(
      '[data-typography-child]'
    ) as HTMLElement | null
    if (inner) {
      inner.style.display = 'block'
      child = inner
    }
  }

  const splitText = splitLines(child, splitTextEngine)

  return {
    timeline: gsap
      .timeline({
        onStart: () => {
          child.classList.add('reveal')
        }
      })
      .fromTo(
        splitText.lines,
        { yPercent: 120 },
        {
          yPercent: 0,
          ease: 'power2.out',
          duration: 1.2,
          stagger: splitText.getStagger(0.1)
        },
        '<'
      ),

    revert: () => {
      // splitText.revert()
    }
  }
}

export const linesExitAnimation = (
  target: HTMLElement,
  reverse?: boolean,
  splitTextEngine: SplitTextEngine = SplitTextEngine.GSAP
): SharedAnimation => {
  // If target is a Typography wrapper ([data-typography]), find its inner
  // [data-typography-child] span and force block display so SplitText can
  // detect line breaks (inline-flex from CSS prevents this).
  let child: HTMLElement = target
  const typographyEl = target.hasAttribute('data-typography')
    ? target
    : target.querySelector('[data-typography]')

  if (typographyEl instanceof HTMLElement) {
    const inner = typographyEl.querySelector(
      '[data-typography-child]'
    ) as HTMLElement | null
    if (inner) {
      inner.style.display = 'block'
      child = inner
    }
  }

  const splitText = splitLines(child, splitTextEngine)

  return {
    timeline: gsap.timeline()
      .fromTo(
        splitText.lines,
        { yPercent: 0 },
        {
          yPercent: -129,
          stagger: splitText.getStagger(0.1)
        }
      ),

    revert: () => {
      // splitText.revert()
    }
  }
}

export const paragraphRevealAnimation = (
  target: HTMLElement,
  reverse: boolean,
  splitTextEngine: SplitTextEngine = SplitTextEngine.GSAP
): SharedAnimation => {
  const splitText = splitLines(target, splitTextEngine)

  return {
    timeline: gsap
      .timeline({
        onComplete: () => {
          if (reverse) return
          splitText.revert()
        }
      })
      .fromTo(
        splitText.instance.lines,
        { yPercent: 120, opacity: 0 },
        {
          opacity: 1,
          yPercent: 0,
          ease: 'power2.out',
          duration: 1.2,
          stagger: splitText.getStagger(0.1)
        }
      ),

    revert: () => {
      splitText.revert()
    }
  }
}

export const mediaZoomRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { scale: 1.2, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1.6, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const mediaFadeRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { opacity: 0 },
        { opacity: 1, duration: 1.2, ease: 'power2.inOut' }
      ),

    revert: () => {
      return
    }
  }
}

export const fadeRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1.2, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const fadeOutAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { opacity: 1, y: 0 },
        { opacity: 0, y: 30, duration: 1.2, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const opacityRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { opacity: 0 },
        { opacity: 1, duration: 1.2, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const charsFadeOutAnimation = (target: HTMLElement): SharedAnimation => {
  const splitText = new SplitText(target, {
    type: 'chars, lines',
    linesClass: 'gsap-line-wrap'
  })

  return {
    timeline: gsap.timeline().to(splitText.chars, {
      yPercent: -120,
      opacity: 0,
      ease: 'power2.inOut',
      duration: 0.6,
      stagger: { each: 0.015, from: 'start' }
    }),

    revert: () => {
      splitText.revert()
    }
  }
}

export const linesFadeOutAnimation = (target: HTMLElement): SharedAnimation => {
  const splitText = new SplitText(target, {
    type: 'lines',
    linesClass: 'gsap-line-wrap'
  })

  const lineInners = splitText.lines.map((line) => {
    const existingInner = line.querySelector(':scope > .gsap-line-inner')

    if (existingInner) {
      return existingInner
    }

    const inner = document.createElement('div')
    inner.className = 'gsap-line-inner'

    while (line.firstChild) {
      inner.appendChild(line.firstChild)
    }

    line.appendChild(inner)

    return inner
  })

  return {
    timeline: gsap.timeline().to(lineInners, {
      yPercent: -120,
      opacity: 0,
      ease: 'power2.inOut',
      duration: 1.2,
      stagger: { each: 0.015, from: 'start' }
    }),

    revert: () => {
      splitText.revert()
    }
  }
}

export const batchRevealAnimation = ({
  targets,
  reverse,
  stagger,
  delay
}: {
  targets: gsap.DOMTarget
  reverse: boolean
  stagger?: gsap.NumberValue | gsap.StaggerVars
  delay?: number
}) => {
  gsap.set(targets, { opacity: 0, y: 30 })

  return ScrollTrigger.batch(targets, {
    once: !reverse,
    start: 'top 75%',
    onEnter: (batch) =>
      gsap.to(batch, {
        opacity: 1,
        y: 0,
        duration: 1.5,
        ease: 'power2.inOut',
        stagger,
        delay
      })
  })
}
